import { useEffect, useState, type FormEvent } from "react";
import { useAuth, useFeedback } from "@taunggyi-plus/auth";
import { ActionIcon, RecordFormDialog } from "@taunggyi-plus/design-system";
import { AdminDirectory } from "@taunggyi-plus/finance-feature";
import { AdminTaxiDrivers } from "./taxi-drivers";

export function AdminPartners() {
  const { client } = useAuth();
  const [, setMessage] = useFeedback();
  const [canCreate, setCanCreate] = useState(false);
  useEffect(() => {
    let active = true;
    void client?.rpc("my_admin_capabilities").then((result) => {
      if (active)
        setCanCreate(!result.error && Array.isArray(result.data) && result.data.includes("all"));
    });
    return () => {
      active = false;
    };
  }, [client]);
  const [open, setOpen] = useState(false);
  const [taxi, setTaxi] = useState(false);
  const [kind, setKind] = useState("hotel");
  const [existing, setExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  function created() {
    setOpen(false);
    setTaxi(false);
    setRevision((value) => value + 1);
    setMessage("Provider added. Manage their service and subscription from Partners.");
  }
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!client || busy) return;
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      const result = await client.rpc("admin_add_provider", {
        service_kind: kind,
        business_name: data.get("business_name"),
        business_address: data.get("address"),
        contact_phone: data.get("phone"),
        owner_identifier: existing ? data.get("owner") : "",
        owner_name: existing ? "" : data.get("name"),
        login_email: existing ? "" : data.get("email"),
        login_username: existing ? "" : data.get("username"),
        initial_password: existing ? "" : data.get("password"),
      });
      if (result.error) throw new Error(result.error.message);
      created();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not add provider. Try again.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <AdminDirectory
        key={revision}
        revision={revision}
        actions={
          canCreate && (
            <button
              type="button"
              className="toolbar-icon-button"
              aria-label="Add provider"
              title="Add provider"
              aria-haspopup="dialog"
              onClick={() => {
                setError("");
                setOpen(true);
              }}
            >
              <ActionIcon action="add" />
            </button>
          )
        }
      />
      {open && (
        <RecordFormDialog open hideTrigger title="Add provider" onClose={() => setOpen(false)}>
          <form className="form-grid" onSubmit={save}>
            <fieldset
              disabled={busy}
              className="form-grid"
              style={{ border: 0, padding: 0, margin: 0, minWidth: 0, gridColumn: "1 / -1" }}
            >
              <label className="field">
                Service
                <select
                  value={kind}
                  onChange={(event) => {
                    setKind(event.target.value);
                    setError("");
                  }}
                >
                  <option value="hotel">Hotel</option>
                  <option value="merchant">Restaurant / Merchant</option>
                  <option value="delivery_fleet">Delivery owner</option>
                  <option value="taxi_group">Taxi driver</option>
                </select>
              </label>
              {kind === "taxi_group" ? (
                <button
                  type="button"
                  className="button"
                  onClick={() => {
                    setOpen(false);
                    setTaxi(true);
                  }}
                >
                  Continue to driver details
                </button>
              ) : (
                <>
                  <label className="field">
                    Business name
                    <input name="business_name" required minLength={2} maxLength={160} />
                  </label>
                  <label className="field">
                    Address
                    <input name="address" required minLength={3} maxLength={500} />
                  </label>
                  <label className="field">
                    Private contact phone
                    <input name="phone" type="tel" required minLength={6} maxLength={30} />
                  </label>
                  <label className="field">
                    Owner account
                    <select
                      value={existing ? "existing" : "new"}
                      onChange={(event) => setExisting(event.target.value === "existing")}
                    >
                      <option value="new">Create new account</option>
                      <option value="existing">Link existing account</option>
                    </select>
                  </label>
                  {existing ? (
                    <label className="field">
                      Owner email or username
                      <input name="owner" required autoComplete="off" />
                    </label>
                  ) : (
                    <>
                      <label className="field">
                        Owner name
                        <input
                          name="name"
                          required
                          minLength={2}
                          maxLength={120}
                          autoComplete="off"
                        />
                      </label>
                      <label className="field">
                        Login email
                        <input name="email" type="email" required autoComplete="off" />
                      </label>
                      <label className="field">
                        Username
                        <input
                          name="username"
                          required
                          pattern="[a-zA-Z0-9_]{3,30}"
                          autoComplete="off"
                        />
                      </label>
                      <label className="field">
                        Initial password
                        <input
                          name="password"
                          type="password"
                          required
                          minLength={12}
                          maxLength={72}
                          autoComplete="new-password"
                        />
                      </label>
                    </>
                  )}
                  <p className="muted">
                    The service starts unpublished. Set its availability and subscription after
                    creating it.
                  </p>
                  {error && <p role="alert">{error}</p>}
                  <button className="button" type="submit" disabled={busy}>
                    {busy ? "Adding…" : "Add provider"}
                  </button>
                </>
              )}
            </fieldset>
          </form>
        </RecordFormDialog>
      )}
      {taxi && <AdminTaxiDrivers createOnly onCreated={created} onClose={() => setTaxi(false)} />}
    </>
  );
}
